import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import DOMPurify from 'dompurify';
import { Bio } from '../../types/aboutBios';

interface BioModalProps {
    bio: Bio | null;
    onClose: () => void;
}

export default function BioModal({ bio, onClose }: BioModalProps) {
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const sanitizedBioText = useMemo(
        () => DOMPurify.sanitize(bio?.bioText ?? ''),
        [bio?.bioText]
    );

    useEffect(() => {
        if (!bio) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        closeButtonRef.current?.focus();

        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = overflow;
        };
    }, [bio, onClose]);

    if (!bio) {
        return null;
    }

    return createPortal(
        <div
            className="team-bio-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-bio-modal-name"
            onClick={onClose}
        >
            <div className="team-bio-modal" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    ref={closeButtonRef}
                    className="team-bio-modal-close"
                    aria-label="Close bio"
                    onClick={onClose}
                >
                    &times;
                </button>
                <div className="team-bio-modal-header">
                    {bio.imageUrl ? (
                        <img
                            src={bio.imageUrl}
                            alt={bio.title}
                            className="team-bio-modal-photo"
                        />
                    ) : (
                        <div className="team-bio-modal-photo-placeholder" aria-hidden="true">
                            <span>{bio.title.charAt(0)}</span>
                        </div>
                    )}
                    <div>
                        {bio.position && (
                            <p className="team-bio-modal-role">{bio.position}</p>
                        )}
                        <h2 id="team-bio-modal-name" className="team-bio-modal-name">
                            {bio.title}
                        </h2>
                    </div>
                </div>
                {sanitizedBioText && (
                    <div
                        className="team-bio-modal-body"
                        dangerouslySetInnerHTML={{ __html: sanitizedBioText }}
                    />
                )}
            </div>
        </div>,
        document.body
    );
}
